/**
 * Reducer for the Document Artifact system.
 *
 * State shape:
 *   state.artifacts = {
 *     [artifactId]: {
 *       content: string,
 *       dirty: boolean,
 *       undoStack: Array<{ content: string, label: string }>,
 *       redoStack: Array<{ content: string, label: string }>,
 *     }
 *   }
 *
 * Supports multiple concurrent artifacts keyed by ID.
 */

import {
  ARTIFACT_LOAD,
  ARTIFACT_COMMIT,
  ARTIFACT_UNDO,
  ARTIFACT_REDO,
  ARTIFACT_SET_DIRTY,
  ARTIFACT_RESET,
} from '../constants/artifact';

const MAX_UNDO_STACK = 50;

const initialArtifactState = {
  content: '',
  dirty: false,
  undoStack: [],
  redoStack: [],
};

const artifactReducer = (state = {}, action) => {
  switch (action.type) {
    case ARTIFACT_LOAD: {
      const { artifactId, content } = action;
      return {
        ...state,
        [artifactId]: {
          ...initialArtifactState,
          ...(state[artifactId] || {}),
          content,
          // Load does not mark dirty (it's setting the "saved" baseline)
          dirty: false,
          // Load resets history — the loaded content is the new starting point
          undoStack: [],
          redoStack: [],
        },
      };
    }

    case ARTIFACT_COMMIT: {
      const { artifactId, content, label } = action;
      const artifact = state[artifactId] || initialArtifactState;

      // Don't create undo entry if content hasn't actually changed
      if (content === artifact.content) return state;

      const newUndo = [
        ...artifact.undoStack,
        { content: artifact.content, label: label || '' },
      ];
      // Cap undo stack size
      if (newUndo.length > MAX_UNDO_STACK) {
        newUndo.shift();
      }

      return {
        ...state,
        [artifactId]: {
          ...artifact,
          content,
          dirty: true,
          undoStack: newUndo,
          redoStack: [],
        },
      };
    }

    case ARTIFACT_UNDO: {
      const { artifactId } = action;
      const artifact = state[artifactId];
      if (!artifact || artifact.undoStack.length === 0) return state;

      const prev = artifact.undoStack[artifact.undoStack.length - 1];
      return {
        ...state,
        [artifactId]: {
          ...artifact,
          content: prev.content,
          undoStack: artifact.undoStack.slice(0, -1),
          redoStack: [
            ...artifact.redoStack,
            { content: artifact.content, label: prev.label },
          ],
          dirty: true,
        },
      };
    }

    case ARTIFACT_REDO: {
      const { artifactId } = action;
      const artifact = state[artifactId];
      if (!artifact || artifact.redoStack.length === 0) return state;

      const next = artifact.redoStack[artifact.redoStack.length - 1];
      return {
        ...state,
        [artifactId]: {
          ...artifact,
          content: next.content,
          redoStack: artifact.redoStack.slice(0, -1),
          undoStack: [
            ...artifact.undoStack,
            { content: artifact.content, label: next.label },
          ],
          dirty: true,
        },
      };
    }

    case ARTIFACT_SET_DIRTY: {
      const { artifactId, dirty } = action;
      const artifact = state[artifactId];
      if (!artifact) return state;

      return {
        ...state,
        [artifactId]: {
          ...artifact,
          dirty,
        },
      };
    }

    case ARTIFACT_RESET: {
      const { artifactId } = action;
      if (!state[artifactId]) return state;

      const newState = { ...state };
      delete newState[artifactId];
      return newState;
    }

    default:
      return state;
  }
};

export default artifactReducer;
